export interface Feature {
  iconPath: string
  title: string
  description: string
}

export interface FeatureCategory {
  category: string
  anchor: string
  description: string
  items: Feature[]
}

export const featureCategories: FeatureCategory[] = [
  {
    category: 'AI-Powered Report Generation',
    anchor: 'reports',
    description:
      'Let artificial intelligence handle the heavy lifting while you focus on client relationships.',
    items: [
      {
        iconPath:
          'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z',
        title: 'Smart Report Writing',
        description:
          'Our AI understands financial advice context and generates compliant, personalised reports based on your meeting notes and client data.',
      },
      {
        iconPath:
          'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
        title: 'Automated Compliance Checking',
        description:
          "Every report is automatically checked against current FCA regulations, COBS rules, and your organisation's policies.",
      },
      {
        iconPath:
          'M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18',
        title: 'Intelligent Suggestions',
        description:
          'Get real-time suggestions for improvements, missing information, and compliance issues as you work.',
      },
    ],
  },
  {
    category: 'Meeting Management',
    anchor: 'meetings',
    description: 'Capture every detail and never miss a follow-up action.',
    items: [
      {
        iconPath:
          'M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z',
        title: 'Audio Recording & Transcription',
        description:
          'Record client meetings and get accurate transcriptions with speaker identification.',
      },
      {
        iconPath:
          'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
        title: 'Automatic Note Generation',
        description:
          'AI generates structured meeting notes, key discussion points, and action items from your recordings.',
      },
      {
        iconPath:
          'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0',
        title: 'Follow-up Tracking',
        description:
          'Never miss a follow-up. Track action items and get reminders for client commitments.',
      },
    ],
  },
  {
    category: 'Document Management',
    anchor: 'planning',
    description:
      'Templates, version control, and automated assembly for efficient document workflows.',
    items: [
      {
        iconPath:
          'M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776',
        title: 'Template Library',
        description:
          'Access hundreds of pre-built templates for every advice scenario, from pensions to protection.',
      },
      {
        iconPath:
          'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10',
        title: 'Custom Templates',
        description:
          'Create and share custom templates across your organisation with version control.',
      },
      {
        iconPath:
          'M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3',
        title: 'Document Assembly',
        description:
          'Automatically assemble complete advice packs with all required documents in the correct order.',
      },
    ],
  },
  {
    category: 'Compliance & Audit',
    anchor: 'compliance',
    description: 'Full audit trails and approval workflows to keep regulators happy.',
    items: [
      {
        iconPath: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Full Audit Trail',
        description:
          'Every change is logged with timestamp, user, and reason. Complete transparency for regulators.',
      },
      {
        iconPath:
          'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z',
        title: 'Approval Workflows',
        description:
          'Configure multi-stage approval workflows with role-based permissions.',
      },
      {
        iconPath:
          'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
        title: 'Compliance Dashboard',
        description:
          'Real-time visibility into compliance status across all your cases and advisers.',
      },
    ],
  },
]
